import { api } from '../axios'
import type { ApiResponse } from '../../../types'

export interface StaffIdCard {
  id: number
  staff_id_number: string
  staff_type: 'teacher' | 'non_teacher'
  staff_ref_id: number
  place_of_posting_id: number
  jurisdiction_type: 'DIRECTORATE' | 'SECRETARIAT'
  issue_date: string
  valid_from: string
  valid_till: string
  validity_years: number
  status: 'draft' | 'approved' | 'active' | 'renewed' | 'expired' | 'revoked'
  qr_code_value: string
  card_pdf_path?: string | null
  created_by: number
  approved_by?: number | null
  approved_at?: string | null
  created_at: string
  updated_at?: string | null
  schools?: {
    school_id: string
    school_name: string
  }
  staff_details?: {
    name: string
    designation: string
    photo_url: string | null
    staff_id?: string | null
    office_name?: string | null
    office_address?: string | null
  }
}

export interface GenerateStaffIdCardRequest {
  staff_type: 'teacher' | 'non_teacher'
  staff_ref_id: number
  /** Legacy: years from today. Use valid_from + valid_till for date range. */
  validity_years?: number
  /** Start date (YYYY-MM-DD) for card validity */
  valid_from?: string
  /** End date (YYYY-MM-DD) for card validity */
  valid_till?: string
}

export interface RenewStaffIdCardRequest {
  /** Legacy: years from today. Use valid_from + valid_till for date range. */
  validity_years?: number
  /** Start date (YYYY-MM-DD) for card validity */
  valid_from?: string
  /** End date (YYYY-MM-DD) for card validity */
  valid_till?: string
}

export interface StaffIdCardListParams {
  page?: number
  limit?: number
  status?: 'draft' | 'approved' | 'active' | 'expired' | 'revoked'
  staff_type?: 'teacher' | 'non_teacher'
  search?: string
}

export interface StaffIdCardListResponse {
  data: StaffIdCard[]
  pagination: {
    page: number
    limit: number
    total: number
    totalPages: number
    hasNext: boolean
    hasPrev: boolean
  }
}

export interface StaffIdCardVerification {
  staff_id_number: string
  name: string
  designation: string
  staff_type: 'teacher' | 'non_teacher'
  type_of_appointment: string
  school_name: string | null
  place_of_posting: string | null
  valid_till: string
  status: string
  photo_url: string | null
}

export const staffIdCardsApi = {
  /**
   * Generate a new staff ID card
   */
  generate: (data: GenerateStaffIdCardRequest) =>
    api.post<ApiResponse<StaffIdCard>>('/staff-id/generate', data),

  /**
   * Approve a staff ID card
   */
  approve: (id: number) =>
    api.post<ApiResponse<StaffIdCard>>(`/staff-id/${id}/approve`),

  /**
   * Renew a staff ID card (creates new record)
   */
  renew: (id: number, data: RenewStaffIdCardRequest) =>
    api.post<ApiResponse<StaffIdCard>>(`/staff-id/${id}/renew`, data),

  /**
   * Revoke a staff ID card
   */
  revoke: (id: number) =>
    api.post<ApiResponse<StaffIdCard>>(`/staff-id/${id}/revoke`),

  /**
   * Get staff ID card by ID
   */
  getById: (id: number) =>
    api.get<ApiResponse<StaffIdCard>>(`/staff-id/${id}`),

  /**
   * List staff ID cards with filtering
   */
  list: (params?: StaffIdCardListParams) =>
    api.get<ApiResponse<StaffIdCardListResponse>>('/staff-id/list', { params }),

  /**
   * Generate/download PDF for staff ID card
   */
  generatePdf: (id: number, forceRegenerate = false) =>
    api.get(`/staff-id/${id}/pdf?force=${forceRegenerate}`, {
      responseType: 'blob'
    }),

  /**
   * Verify staff ID card by staff_id_number (public endpoint, no auth)
   */
  verify: (staffIdNumber: string) =>
    api.get<ApiResponse<StaffIdCardVerification>>(`/verify/staff-id/${staffIdNumber}`)
}
